import { Intersection, Points, Vector3 } from 'three';

import { isDefined } from '../../../../../utils/variables';
import { AttributeData, AttributeId } from '../../skymap-extras';
import { DesignNode, Revert } from '../design-node';
import { FaceId } from './face-node';
import { LineSegmentId, LineSegmentNode } from './line-segment-node';

export type PointId = number & { __type: 'PointId' };

export type PointNode = {
  id: PointId;
  vertex: Vector3;
  faces: FaceId[];
  segments: LineSegmentId[];
  attributes: Map<AttributeId, AttributeData>;
  /**
   * The center point of the circle or ellipse that the point is a seam of: a point that only
   * joins two of its arcs, see circle.ts. The key is always set, so that reverting a change
   * restores a point that is no seam.
   */
  seamOf: PointId | undefined;
};

export function getPointNeighbours(
  point: PointNode,
  designNode: DesignNode,
  filter?: {
    pointIdMap?: Map<PointId, boolean>;
    lineSegmentFilter?: (node: LineSegmentNode) => boolean;
  },
): PointId[] {
  const lineSegmentFilter = filter?.lineSegmentFilter ? filter.lineSegmentFilter : () => true;

  const pointIdFilter = isDefined(filter?.pointIdMap)
    ? (id: PointId) => filter.pointIdMap!.get(id) === true
    : () => true;

  return point.segments
    .map((x) => designNode.getLineSegmentById(x))
    .filter(lineSegmentFilter)
    .map((x) => (x.values[0] === point.id ? x.values[1] : x.values[0]))
    .filter(pointIdFilter);
}

function pointHasNeighbours(designNode: DesignNode, id: PointId) {
  return designNode.getPointById(id).segments.length > 0;
}

function isPointPartOfFace(node: DesignNode, id: PointId) {
  return node.getPointById(id).faces.length ?? 0 > 0;
}

/**
 * A point is part of a breakline if it is connected to 2 or more faces.
 */
export function isPointPartOfBreakline(node: DesignNode, id: PointId) {
  return node.getPointById(id).faces.length ?? 0 ?? 0 > 1;
}

export function isPointPartOfLine(node: DesignNode, id: PointId) {
  return pointHasNeighbours(node, id) && !isPointPartOfFace(node, id);
}

export function findPointIdFromIntersection(
  node: DesignNode,
  intersection?: Intersection,
): PointId | undefined {
  return intersection?.object instanceof Points && isDefined(intersection.index)
    ? node.getPointByIndex(intersection.index).id
    : undefined;
}

export function addPoints(
  node: DesignNode,
  vectors: Vector3[],
  attributeData?: Map<AttributeId, AttributeData>,
): Revert<'remove-point'>[] {
  const changes: Revert<'remove-point'>[] = [];
  for (const vector of vectors) {
    changes.unshift(node.addPoint(vector, attributeData));
  }
  return changes;
}
